/**
 * Links from the paid-traffic landing pages into the existing calculator.
 *
 * Two jobs, and nothing else:
 *  1. Carry the ad click identifiers and campaign tags across, so a visitor who
 *     arrives on /bookkeeping-fees?gclid=... still reaches the calculator with
 *     that attribution intact and Google can tie the eventual quote back to the
 *     click.
 *  2. Name the service the page is about, so the calculator can open on the
 *     right wizard instead of making the visitor choose again.
 *
 * The calculator's own pricing, questions and quote logic are untouched - this
 * only decides the URL we send people to.
 */

/** Click identifiers worth preserving, beyond anything starting with utm_. */
const ATTRIBUTION_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "gclsrc",
  "gad_source",
  "msclkid",
  "fbclid",
  "campaignid",
  "adgroupid",
  "keyword",
  "matchtype",
  "device",
];

const worthKeeping = (key) => {
  const lower = key.toLowerCase();
  return lower.startsWith("utm_") || ATTRIBUTION_KEYS.includes(lower);
};

/** The calculator route, which must not change. */
export const CALCULATOR_PATH = "/pricing-calculator";

/** The outsourced finance page, which is scoped by a person, never priced here. */
export const FINANCE_PATH = "/outsourced-financial-management";

/**
 * The scoping form's revenue bands, as upper bounds.
 *
 * Mirrors `REVENUE_BANDS` in the backend's finance vocabulary so a figure typed
 * into the calculator can arrive at the brief already answered.
 */
const FINANCE_REVENUE_BANDS = [
  [20_000_000, "under_r20m"],
  [30_000_000, "r20m_r30m"],
  [50_000_000, "r30m_r50m"],
  [100_000_000, "r50m_r100m"],
];

/** The band a typed annual turnover falls into. */
export const financeRevenueBand = (turnover) => {
  const amount = Number(turnover);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const found = FINANCE_REVENUE_BANDS.find(([upper]) => amount <= upper);
  return found ? found[1] : "r100m_plus";
};

/**
 * Where "Scope my finance function" should send somebody.
 *
 * Carries the ad parameters across exactly as the calculator links do, answers
 * the revenue question from the figure they already typed, and lands on the
 * brief itself rather than the top of the page.
 */
export const financeScopeHref = (turnover = null, search = "") => {
  const incoming = new URLSearchParams(
    search || (typeof window === "undefined" ? "" : window.location.search),
  );
  const next = new URLSearchParams();

  const band = financeRevenueBand(turnover);
  if (band) next.set("revenue", band);
  incoming.forEach((value, key) => {
    if (["service", "revenue"].includes(key.toLowerCase())) return;
    if (worthKeeping(key)) next.set(key, value);
  });

  const query = next.toString();
  return `${FINANCE_PATH}${query ? `?${query}` : ""}#scope`;
};

/**
 * Ad-friendly slugs mapped to the calculator's own service keys.
 * The keys on the right are the calculator's and are never invented here.
 */
export const SERVICE_SLUGS = {
  bookkeeping: "bookkeeping",
  "financial-statements": "afs",
  "audit-review": "assurance",
};

/** The calculator key a `?service=` value asks for, or null if unrecognised. */
export const serviceKeyFromSlug = (slug) =>
  SERVICE_SLUGS[String(slug || "").trim().toLowerCase()] || null;

/**
 * Where a landing-page call to action should point.
 *
 * @param {string|null} serviceSlug one of the SERVICE_SLUGS keys
 * @param {string} search the current location.search, so ad parameters survive
 */
export const calculatorHref = (serviceSlug = null, search = "") => {
  const incoming = new URLSearchParams(
    search || (typeof window === "undefined" ? "" : window.location.search),
  );
  const next = new URLSearchParams();

  if (serviceSlug) next.set("service", serviceSlug);
  incoming.forEach((value, key) => {
    if (key.toLowerCase() === "service") return;
    if (worthKeeping(key)) next.set(key, value);
  });

  const query = next.toString();
  return query ? `${CALCULATOR_PATH}?${query}` : CALCULATOR_PATH;
};

export const PHONE_DISPLAY = "081 788 2993";
export const PHONE_HREF = "tel:+27817882993";
export const EMAIL_ADDRESS = "info@t-tite.co.za";
export const EMAIL_HREF = "mailto:info@t-tite.co.za";
